// VolunteerDetailPage.jsx
import React, { useState, useEffect } from 'react';
import { MdCalendarMonth, MdDeleteOutline, MdArrowBack } from "react-icons/md";
import { PiNotePencil } from "react-icons/pi";
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { deleteVolunteer, getEventAccessLogs, getEventAnalytics, getVolunteerById, getVolunteers } from '../../../../Redux/Reducers/communityReducer';
import VolunterRemoveConfrim from './VolunterRemoveConfrim';
import VolunterLogsModal from './VolunterLogsModal';

const VolunteerDetailPage = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { eventId, volunteerId } = useParams(); // Get IDs from URL params

    const [showConfirmation, setShowConfirmation] = useState(false);
    const [showLogsModal, setShowLogsModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Get data from Redux
    const VolunteerByIdData = useSelector((state) => state?.community?.VolunteerByIdData);
    const GetEventAccessById = useSelector((state) => state?.community?.geteventaccessbyId);
    const singleEvent = useSelector((state) => state?.community?.singleEvent);

    // Fetch volunteer details when component mounts
    useEffect(() => {
        if (volunteerId) {
            dispatch(getVolunteerById(volunteerId));
        }
    }, [dispatch, volunteerId]);

    const person = VolunteerByIdData;
    const eventData = singleEvent;

    const handleGoBack = () => {
        navigate(-1); // Go back to previous page
    };

    const handleLogsClick = () => {
        if (person?.id && eventData?.id) {
            dispatch(getEventAccessLogs({
                eventId: eventData?.id,
                volunteer_id: person?.id
            }));
            setShowLogsModal(true);
        }
    };

    const handleRemoveClick = () => {
        setShowConfirmation(true);
    };

    const handleConfirmDelete = async () => {
        try {
            setIsDeleting(true);

            if (person?.id && eventData?.id) {
                const response = await dispatch(deleteVolunteer(person?.id));
                if (response?.payload?.statusCode === 200) {
                    await dispatch(getVolunteers(eventData?.id));
                    await dispatch(getEventAnalytics(eventData?.id));
                    setIsDeleting(false);
                    setShowConfirmation(false);
                    // Navigate back to event dashboard after successful deletion
                    navigate(`/community-dashboard`);
                } else {
                    console.error("Delete failed with status:", response?.payload?.statusCode);
                    setIsDeleting(false);
                }
            }
        } catch (error) {
            console.error("Error in handleConfirmDelete:", error);
            setIsDeleting(false);
        }
    };

    const handleCancelDelete = () => {
        setShowConfirmation(false);
    };

    const handleCloseLogsModal = () => {
        setShowLogsModal(false);
    };

    if (!person || !eventData) {
        return (
            <div style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100vh",
                background: "#f5f7fa"
            }}>
                Loading...
            </div>
        );
    }

    // Get event details
    const eventName = eventData?.title || eventData?.event_title || "Event Name";
    const eventDate = eventData?.start_time ? new Date(eventData.start_time).toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    }) : "Date not specified";
    const eventLocation = eventData?.event_location || "Location not specified";

    // Get person details
    const personName = person?.name || 'Unknown';
    const personEmail = person?.email || 'No email';
    const personPhone = person?.phone || 'No phone';
    const personProfile = person?.profile || person?.profile_url || "https://static.vecteezy.com/system/resources/thumbnails/009/292/244/small/default-avatar-icon-of-social-media-user-vector.jpg";

    return (
        <>
            <div style={{
                minHeight: "100vh",
                background: "#f5f7fa",
                padding: "1rem",
                display: "flex",
                flexDirection: "column",
                alignItems: "center", // Change back to "center"
                width: "100%", // Ensure full width
            }}>
                {/* Back Button - Now properly left-aligned */}
                <button
                    onClick={handleGoBack}
                    style={{
                        background: "white",
                        border: "none",
                        borderRadius: "30px",
                        padding: "0.4rem 1rem",
                        fontSize: "0.85rem",
                        fontWeight: 600,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.3rem",
                        color: "#0B1C33",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        marginBottom: "1rem",
                        marginLeft: "1rem", // Added explicit left margin
                        alignSelf: "flex-start", // This keeps it left-aligned
                        marginLeft: "0", // Remove the left margin
                    }}
                >
                    <MdArrowBack style={{ fontSize: "16px" }} /> Back
                </button>

                {/* Main Content Card - Centered */}
                <div style={{
                    maxWidth: "450px",
                    width: "100%",
                    margin: "0 auto", // This centers the card
                    alignSelf: "center", // Ensures card is centered
                }}>
                    <div
                        style={{
                            background: "#FFFFFF",
                            borderRadius: "16px",
                            overflow: "hidden",
                            boxShadow: "0 10px 25px -8px rgba(0,0,0,0.15)",
                        }}
                    >
                        {/* Header with Feature Image - Reduced height */}
                        <div
                            style={{
                                position: "relative",
                                height: "100px",
                                background: `url(${eventData?.feature_image || 'https://via.placeholder.com/450x100?text=Event+Background'})`,
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                            }}
                        >
                            {/* Gradient Overlay */}
                            <div
                                style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: "linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.7) 100%)",
                                }}
                            />

                            {/* Profile Image - Smaller and positioned better */}
                            <div
                                style={{
                                    position: "absolute",
                                    bottom: "-30px",
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    width: "70px",
                                    height: "70px",
                                    borderRadius: "50%",
                                    border: "3px solid white",
                                    overflow: "hidden",
                                    background: "#E9EDF4",
                                    boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
                                    zIndex: 20,
                                }}
                            >
                                <img
                                    src={personProfile}
                                    alt={personName}
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                    }}
                                    onError={(e) => {
                                        e.target.src = "https://static.vecteezy.com/system/resources/thumbnails/009/292/244/small/default-avatar-icon-of-social-media-user-vector.jpg";
                                    }}
                                />
                            </div>
                        </div>

                        {/* Content - Reduced padding */}
                        <div style={{ padding: "45px 20px 20px 20px" }}>
                            {/* User Info - Compact */}
                            <div style={{ textAlign: "center", marginBottom: "16px" }}>
                                <h1 style={{
                                    fontSize: "1.5rem",
                                    fontWeight: 700,
                                    color: "#0B1C33",
                                    margin: "0 0 4px 0",
                                    letterSpacing: "-0.01em",
                                }}>
                                    {personName}
                                </h1>
                                <div style={{
                                    fontSize: "0.85rem",
                                    color: "#0044C6",
                                    marginBottom: "2px",
                                    wordBreak: "break-all",
                                }}>
                                    {personEmail}
                                </div>
                                {personPhone && (
                                    <div style={{
                                        fontSize: "0.8rem",
                                        color: "#6F7D95",
                                        marginTop: "2px",
                                    }}>
                                        {personPhone}
                                    </div>
                                )}
                            </div>

                            {/* Divider - Thinner */}
                            <div style={{
                                height: "1px",
                                background: "linear-gradient(90deg, transparent, #E9EDF4, transparent)",
                                margin: "16px 0",
                            }} />

                            {/* Event Details - Compact */}
                            <div style={{ marginBottom: "20px" }}>
                                <h2 style={{
                                    fontSize: "1rem",
                                    fontWeight: 600,
                                    color: "#0B1C33",
                                    margin: "0 0 10px 0",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                }}>
                                    <MdCalendarMonth style={{ fontSize: "18px" }} /> Assigned Event
                                </h2>
                                <div style={{
                                    background: "#F8FAFE",
                                    borderRadius: "12px",
                                    padding: "12px",
                                }}>
                                    <div style={{ marginBottom: "8px" }}>
                                        <div style={{ fontSize: "0.7rem", color: "#6F7D95", marginBottom: "2px" }}>Event Name</div>
                                        <div style={{ fontSize: "0.9rem", fontWeight: 500, color: "#0B1C33" }}>{eventName}</div>
                                    </div>
                                    <div style={{ marginBottom: "8px" }}>
                                        <div style={{ fontSize: "0.7rem", color: "#6F7D95", marginBottom: "2px" }}>Date</div>
                                        <div style={{ fontSize: "0.9rem", fontWeight: 500, color: "#0B1C33" }}>{eventDate}</div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: "0.7rem", color: "#6F7D95", marginBottom: "2px" }}>Location</div>
                                        <div style={{ fontSize: "0.9rem", fontWeight: 500, color: "#0B1C33" }}>{eventLocation}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons - Compact and side by side */}
                            <div style={{
                                display: "flex",
                                gap: "10px",
                                justifyContent: "center",
                                marginTop: "15px",
                            }}>
                                <button
                                    onClick={handleLogsClick}
                                    style={{
                                        flex: 1,
                                        padding: "10px 0",
                                        background: "#0044C6",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "30px",
                                        fontSize: "0.85rem",
                                        fontWeight: 600,
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "6px",
                                        transition: "all 0.2s",
                                        boxShadow: "0 2px 8px rgba(0,68,198,0.2)",
                                        maxWidth: "150px",
                                    }}
                                >
                                    <PiNotePencil style={{ fontSize: "16px" }} /> Logs
                                </button>
                                <button
                                    onClick={handleRemoveClick}
                                    disabled={isDeleting}
                                    style={{
                                        flex: 1,
                                        padding: "10px 0",
                                        background: "white",
                                        color: "#DC2626",
                                        border: "1.5px solid #DC2626",
                                        borderRadius: "30px",
                                        fontSize: "0.85rem",
                                        fontWeight: 600,
                                        cursor: isDeleting ? "not-allowed" : "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "6px",
                                        transition: "all 0.2s",
                                        opacity: isDeleting ? 0.6 : 1,
                                        maxWidth: "150px",
                                    }}
                                >
                                    <MdDeleteOutline style={{ fontSize: "16px" }} />
                                    {isDeleting ? "..." : "Remove"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirmation Modal */}
            <VolunterRemoveConfrim
                isOpen={showConfirmation}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                title="Remove Volunteer"
                message={`Are you sure you want to remove ${personName} from this event?`}
                confirmText="Remove"
                cancelText="Cancel"
                isLoading={isDeleting}
            />

            {/* Logs Modal */}
            <VolunterLogsModal
                isOpen={showLogsModal}
                onClose={handleCloseLogsModal}
                personName={personName}
                eventName={eventName}
                logsData={GetEventAccessById}
            />
        </>
    );
};

export default VolunteerDetailPage;